import type { SaveState } from "../game/save.ts";
import {
  PROTO, type ClientMsg, type DuelInfo, type Listing, type PeerInfo, type RaidView, type ServerMsg, type TradeView,
} from "./protocol.ts";
import type { CrownHouseView, EconView, RaidKit } from "./economy.ts";
import type { WalletChain, WalletLink } from "./wallet.ts";
import { walletAddress, walletError, walletSign } from "./walletBrowser.ts";

/** Reads an address and signs text; the browser wallets by default, a fake in tests. */
export interface WalletSigner {
  address(chain: WalletChain): Promise<string>;
  sign(chain: WalletChain, address: string, message: string): Promise<string>;
}

const browserSigner: WalletSigner = { address: walletAddress, sign: walletSign };

export type NetStatus = "offline" | "connecting" | "online" | "denied";

/** A remote scout as the renderer sees it: the tile it is walking toward plus a step clock. */
export interface Peer extends PeerInfo {
  fromX: number;
  fromY: number;
  stepT: number;
  stepMs: number;
  moving: number;
  chat: { text: string; at: number } | null;
  emote: { e: number; at: number } | null;
}

export interface Request {
  kind: "duel" | "trade";
  from: string;
  name: string;
  at: number;
}

interface AccountKey {
  id: string;
  secret: string;
  rev: number;
}

export const ACCOUNT_KEY = "memecraft.account.v1";
const REF_KEY = "memecraft.ref";

/** The invite tag this browser arrived with (?ref=TAG), kept until the account exists. */
function inviteTag(): string | undefined {
  try {
    const q = typeof window !== "undefined" ? new URLSearchParams(window.location?.search ?? "").get("ref") : null;
    if (q && /^[A-Z0-9]{4,8}$/i.test(q)) localStorage.setItem(REF_KEY, q.toUpperCase());
    return localStorage.getItem(REF_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function randomHex(bytes: number): string {
  const a = new Uint8Array(bytes);
  (globalThis.crypto ?? { getRandomValues: (x: Uint8Array) => x.map(() => Math.floor(Math.random() * 256)) }).getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function loadAccount(): AccountKey {
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY);
    if (raw) {
      const a = JSON.parse(raw) as AccountKey;
      if (typeof a.id === "string" && typeof a.secret === "string") return { id: a.id, secret: a.secret, rev: Number(a.rev) || 0 };
    }
  } catch {
    /* blocked storage: a fresh guest key for this tab */
  }
  const a = { id: `u-${randomHex(12)}`, secret: randomHex(24), rev: 0 };
  saveAccount(a);
  return a;
}

/** The account whose recovery key this device already copied (the save-your-progress reminder stops). */
const KEY_SAVED = "memecraft.keySaved";

/** Where the site keeps a guest key it set aside, so "Back to my guest scout" can bring it back. */
const PREV_KEY = "memecraft.account.prev";

/** Set this browser's own key aside before it switches to another scout (only if it really was another). */
function rememberPrev(prev: AccountKey, next: string): void {
  if (prev.id === next) return;
  try {
    localStorage.setItem(PREV_KEY, JSON.stringify(prev));
  } catch {
    /* ignore */
  }
}

/** Read and remove ?handoff= from this page's address, so a reload never replays it. */
export function takeHandoffParam(): string | null {
  if (typeof window === "undefined" || !window.location?.href) return null;
  try {
    const u = new URL(window.location.href);
    const code = u.searchParams.get("handoff");
    if (!code) return null;
    u.searchParams.delete("handoff");
    window.history?.replaceState?.(null, "", u.pathname + u.search + u.hash);
    return /^[0-9a-f]{32}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

function saveAccount(a: AccountKey): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(a));
  } catch {
    /* ignore */
  }
}

/** Where the live world is: `?server=` wins, fixtures stay offline, vite dev talks to :8787. */
export function serverUrl(): string | null {
  const loc = typeof window !== "undefined" ? window.location : undefined;
  if (!loc) return null;
  const q = new URLSearchParams(loc.search ?? "");
  const explicit = q.get("server");
  if (explicit === "off") return null;
  if (explicit) return explicit;
  if (q.get("shot")) return null;
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_SERVER_URL;
  // Static single-player builds (e.g. VITE_SERVER_URL=off vite build) never open a socket.
  if (env === "off") return null;
  if (env) return env;
  if (!loc.host || loc.protocol === "file:" || typeof WebSocket === "undefined") return null;
  if (loc.port === "5173" || loc.port === "4173") return `ws://${loc.hostname}:8787/ws`;
  return `${loc.protocol === "https:" ? "wss" : "ws"}://${loc.host}/ws`;
}

export interface NetHost {
  /** Called when the server's copy of the save must replace the local one. */
  adoptSave(state: SaveState, why: string): void;
  now: number;
}

/**
 * Browser side of the live world. Everything degrades to offline: no URL, a refused socket or a
 * sandboxed page simply leaves `status = "offline"` and the single-player game runs unchanged.
 */
export class Net {
  status: NetStatus = "offline";
  reason = "";
  readonly url: string | null;
  id = "";
  name = "";
  /** The server-picked tag that tells same-named scouts apart. */
  tag = "";
  rating = 1000;
  wins = 0;
  losses = 0;
  online = 0;
  readonly peers = new Map<string, Peer>();
  chatLog: { name: string; text: string; at: number; self: boolean }[] = [];
  requests: Request[] = [];
  notices: string[] = [];
  duel: DuelInfo | null = null;
  trade: TradeView | null = null;
  top: { name: string; rating: number; wins: number; losses: number }[] = [];
  /** Latest market listings (null until the Market is opened). */
  market: Listing[] | null = null;
  /** Latest Raid Gate view (null until the gate is visited). */
  raid: RaidView | null = null;
  /** Crowns & Brass: this scout's wallet and vault, the Crown House, and the rentals just dealt. */
  econ: EconView | null = null;
  house: CrownHouseView | null = null;
  kit: RaidKit | null = null;
  /** The crypto wallet linked to this account (identity only), and whether the server offers wallets. */
  wallet: WalletLink | null = null;
  walletsEnabled = false;
  /** The server turned us away because it is full; the normal reconnect keeps trying. */
  full = false;
  /** Turnstile site key when the server wants a bot check before registering. */
  captchaKey = "";
  /** Set while a wallet prompt is open, so a second press does not stack prompts. */
  walletBusy = false;
  private ws: WebSocket | null = null;
  private account: AccountKey | null = null;
  private listeners = new Set<(msg: ServerMsg) => void>();
  private pendingSave: SaveState | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private inflight = false;
  private retry = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private wanted: (() => SaveState) | null = null;
  /** A recovery key being tried: kept off disk until the server accepts it, with the key to fall back to. */
  private restoring: { prev: AccountKey; status: NetStatus; reason: string } | null = null;
  /** A handoff code this page was opened with (?handoff=), claimed before the first hello. */
  private claim: string | null = null;
  /** A handoff code fetched ahead of a tap, so the jump to the wallet app stays inside the tap. */
  private handoff: { code: string; at: number } | null = null;
  private lastMove = "";
  private pingT = 0;

  constructor(private host: NetHost, url: string | null = serverUrl()) {
    this.url = url;
    this.claim = takeHandoffParam();
  }

  get enabled(): boolean {
    return this.url !== null;
  }

  get live(): boolean {
    return this.status === "online";
  }

  on(fn: (msg: ServerMsg) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Join the world with this save (idempotent). `current` is read again on every reconnect. */
  join(current: () => SaveState): void {
    this.wanted = current;
    if (!this.url || this.ws || this.status === "denied") return;
    if (this.claim) {
      const code = this.claim;
      this.claim = null;
      void this.claimHandoff(code);
      return;
    }
    this.open();
  }

  private open(): void {
    if (!this.url || !this.wanted) return;
    this.account ??= loadAccount();
    this.status = "connecting";
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.status = "offline";
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      const save = this.wanted!();
      // A restore signs in as the other account without sending this device's save or name.
      if (this.restoring) this.send({ t: "hello", v: PROTO, id: this.account!.id, secret: this.account!.secret, name: "", save: null, rev: 0, restore: true });
      else this.send({ t: "hello", v: PROTO, id: this.account!.id, secret: this.account!.secret, name: save.name, save, rev: this.account!.rev, ref: inviteTag() });
    };
    ws.onmessage = (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMsg;
      } catch {
        return;
      }
      this.receive(msg);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.duel || this.trade) this.notices.push("Connection lost.");
      this.reset();
      if (this.status === "denied") return;
      this.status = "offline";
      const wait = Math.min(30_000, 1500 * 2 ** this.retry++);
      this.retryTimer = setTimeout(() => this.open(), wait);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  /**
   * The recovery key: this account's id and secret in one line. Brass and Crowns live on the
   * server under this key, so writing it down is what lets a scout get them back on a new device.
   */
  recoveryKey(): string {
    this.account ??= loadAccount();
    return `MC1-${this.account.id.slice(2)}-${this.account.secret}`;
  }

  /** This scout's recovery key was copied on this device (or a wallet guards it): no need to nag. */
  keySaved(): boolean {
    if (this.wallet) return true;
    this.account ??= loadAccount();
    try {
      return localStorage.getItem(KEY_SAVED) === this.account.id;
    } catch {
      return false;
    }
  }

  markKeySaved(): void {
    this.account ??= loadAccount();
    try {
      localStorage.setItem(KEY_SAVED, this.account.id);
    } catch {
      /* ignore */
    }
  }

  /**
   * Try the account behind a recovery key. The key is kept only in memory until the server accepts
   * it: a typo falls back to this device's own account and changes nothing. False if unreadable.
   */
  restore(key: string): boolean {
    const m = /MC1-([0-9a-f]{24})-([0-9a-f]{48})/i.exec(key.replace(/\s+/g, ""));
    if (!m) return false;
    const a = { id: `u-${m[1]!.toLowerCase()}`, secret: m[2]!.toLowerCase(), rev: 0 };
    const prev = this.account ?? loadAccount();
    if (a.id === prev.id && a.secret === prev.secret) return true;
    this.restoring = this.restoring ?? { prev, status: this.status, reason: this.reason };
    this.account = a;
    this.reconnect();
    return true;
  }

  /**
   * Link a wallet to this account: the wallet shares its address, the server hands back a one-time
   * message, the wallet signs it. The outcome arrives as a notice (and a "wallet" message).
   */
  async linkWallet(chain: WalletChain, signer: WalletSigner = browserSigner): Promise<void> {
    if (this.walletBusy) return;
    if (!this.live) return this.say("Connect to the live world first.");
    this.walletBusy = true;
    try {
      const address = await signer.address(chain);
      // A refusal (wallets off, a bad address) comes back as a notice instead of a challenge.
      const wait = this.next((m) => (m.t === "walletChallenge" && m.purpose === "link") || m.t === "notice", 15_000);
      this.send({ t: "walletChallenge", chain, address, purpose: "link" });
      const ch = await wait;
      if (!ch) return this.say("The server did not answer. Try again.");
      if (ch.t !== "walletChallenge") return;
      const signature = await signer.sign(chain, ch.address, ch.message);
      this.send({ t: "walletLink", signature });
    } catch (e) {
      this.say(walletError(e));
    } finally {
      this.walletBusy = false;
    }
  }

  /** This scout's invite link: the game's address with their tag (counted on the server, never paid). */
  inviteLink(): string | null {
    if (!this.tag || typeof window === "undefined") return null;
    return `${window.location.origin}/play?ref=${this.tag}`;
  }

  /** Send a bug report (the server attaches the save). False when offline. */
  reportBug(text: string): boolean {
    const agent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    return this.send({ t: "bugReport", text, agent });
  }

  unlinkWallet(): void {
    this.send({ t: "walletUnlink" });
  }

  /**
   * Sign in with a linked wallet (a new device, a lost key). Runs on a side connection before any
   * hello; the server answers with the account and a fresh key, which this device then restores.
   */
  async walletLogin(chain: WalletChain, signer: WalletSigner = browserSigner): Promise<void> {
    if (this.walletBusy) return;
    if (!this.url || typeof WebSocket === "undefined") return this.say("Wallet sign-in needs the live world.");
    this.walletBusy = true;
    let ws: WebSocket | null = null;
    try {
      const address = await signer.address(chain);
      ws = new WebSocket(this.url);
      const inbox: ServerMsg[] = [];
      let wake: (() => void) | null = null;
      ws.onmessage = (ev) => {
        try {
          inbox.push(JSON.parse(String(ev.data)) as ServerMsg);
        } catch {
          return;
        }
        wake?.();
      };
      const sock = ws;
      await new Promise<void>((ok, fail) => {
        sock.onopen = () => ok();
        sock.onerror = () => fail(new Error("Could not reach the server."));
      });
      const take = async (want: (m: ServerMsg) => boolean, ms: number): Promise<ServerMsg | null> => {
        const end = Date.now() + ms;
        for (;;) {
          const i = inbox.findIndex((m) => want(m) || m.t === "notice");
          if (i >= 0) return inbox.splice(i, 1)[0]!;
          if (Date.now() > end) return null;
          await new Promise<void>((r) => {
            wake = r;
            setTimeout(r, 250);
          });
          wake = null;
        }
      };
      sock.send(JSON.stringify({ t: "walletChallenge", chain, address, purpose: "login" } satisfies ClientMsg));
      const ch = await take((m) => m.t === "walletChallenge", 15_000);
      if (!ch || ch.t !== "walletChallenge") return this.say(ch?.t === "notice" ? ch.text : "The server did not answer. Try again.");
      const signature = await signer.sign(chain, ch.address, ch.message);
      sock.send(JSON.stringify({ t: "walletLogin", signature } satisfies ClientMsg));
      const res = await take((m) => m.t === "walletKey", 15_000);
      if (!res || res.t !== "walletKey") return this.say(res?.t === "notice" ? res.text : "The server did not answer. Try again.");
      // An empty secret means this device's own key is still the right one.
      if (res.secret) this.adoptKey(res.id, res.secret);
      else this.say(`Signed in as ${res.name}.`);
    } catch (e) {
      this.say(walletError(e));
    } finally {
      ws?.close();
      this.walletBusy = false;
    }
  }

  /** Fetch a handoff code now (opening the wallet menu), so a later tap can jump to the wallet app at once. */
  prepareHandoff(): void {
    if (!this.live || (this.handoff && Date.now() - this.handoff.at < 5 * 60_000)) return;
    this.send({ t: "handoff" });
    void this.next((m) => m.t === "handoff", 8000).then((m) => {
      if (m?.t === "handoff") this.handoff = { code: m.code, at: Date.now() };
    });
  }

  /** The prepared handoff code, while it is still fresh (the server keeps it 10 minutes). */
  handoffCode(): string | null {
    return this.handoff && Date.now() - this.handoff.at < 8 * 60_000 ? this.handoff.code : null;
  }

  /** This page came from the same phone's other browser with a handoff code: become that scout. */
  private async claimHandoff(code: string): Promise<void> {
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(this.url!);
      const sock = ws;
      const reply = await new Promise<ServerMsg | null>((resolve) => {
        const timer = setTimeout(() => resolve(null), 10_000);
        sock.onopen = () => sock.send(JSON.stringify({ t: "handoffClaim", code } satisfies ClientMsg));
        sock.onmessage = (ev) => {
          try {
            const m = JSON.parse(String(ev.data)) as ServerMsg;
            if (m.t === "walletKey" || m.t === "notice") {
              clearTimeout(timer);
              resolve(m);
            }
          } catch {
            /* ignore */
          }
        };
        sock.onerror = () => {
          clearTimeout(timer);
          resolve(null);
        };
      });
      if (reply?.t === "walletKey" && reply.secret) {
        rememberPrev(this.account ?? loadAccount(), reply.id);
        return this.adoptKey(reply.id, reply.secret);
      }
      if (reply?.t === "notice") this.say(reply.text);
    } catch {
      /* no socket: carry on as this browser's own scout */
    } finally {
      ws?.close();
    }
    if (!this.ws) this.open();
  }

  /** Switch this device to an account key the server just issued (kept off disk until welcome). */
  private adoptKey(id: string, secret: string): void {
    const prev = this.account ?? loadAccount();
    this.restoring = this.restoring ?? { prev, status: this.status, reason: this.reason };
    this.account = { id, secret, rev: 0 };
    // A device stopped by "signed in elsewhere" may come back now: this is the user's own choice.
    if (this.status === "denied") this.restoring.status = "offline";
    this.status = "offline";
    this.reconnect();
  }

  /** The next server message that passes `want`, or null after `ms`. */
  private next(want: (m: ServerMsg) => boolean, ms: number): Promise<ServerMsg | null> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        off();
        resolve(null);
      }, ms);
      const off = this.on((m) => {
        if (!want(m)) return;
        clearTimeout(timer);
        off();
        resolve(m);
      });
    });
  }

  /** Forget everything tied to the current connection (peers, requests, in-flight saves, fights). */
  private reset(): void {
    this.peers.clear();
    this.requests = [];
    this.inflight = false;
    this.duel = null;
    this.trade = null;
  }

  /** A client-side notice, delivered like a server one so open screens can show it. */
  private say(text: string): void {
    this.receive({ t: "notice", text });
  }

  private reconnect(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.reset();
    this.pendingSave = null;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = null;
    // Another account: nothing on screen from the old one may stay.
    this.econ = null;
    this.house = null;
    this.kit = null;
    this.raid = null;
    this.market = null;
    this.wallet = null;
    this.status = "offline";
    this.retry = 0;
    this.open();
  }

  close(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.status = "offline";
  }

  send(msg: ClientMsg): boolean {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  /** Test/driver entry point: feed one server message as if it arrived on the socket. */
  receive(msg: ServerMsg): void {
    const now = this.host.now;
    switch (msg.t) {
      case "welcome":
        this.status = "online";
        this.retry = 0;
        this.id = msg.id;
        this.name = msg.name;
        this.tag = msg.tag ?? "";
        this.rating = msg.rating;
        this.wins = msg.wins;
        this.losses = msg.losses;
        this.online = msg.online;
        this.wallet = msg.wallet ?? null;
        this.walletsEnabled = !!msg.wallets;
        this.full = false;
        this.captchaKey = msg.captcha ?? "";
        if (this.restoring) {
          // The server accepted the key: it becomes this device's account, and its save replaces ours.
          this.restoring = null;
          this.setRev(msg.rev);
          if (msg.save) this.host.adoptSave(msg.save, "restore");
          this.say(`Restored ${msg.name}. This device now plays that account.`);
        } else {
          this.setRev(msg.rev);
          if (msg.save) this.host.adoptSave(msg.save, "welcome");
        }
        // The wallet and showcase (for this scout's Crown follower).
        this.send({ t: "econ" });
        break;
      case "denied":
        if (this.restoring) {
          // Back to this device's own account. A wrong key changes nothing; any other refusal is said as is.
          const back = this.restoring;
          this.account = back.prev;
          this.restoring = null;
          const wrongKey = /does not match|Start a game first|Bad account/.test(msg.reason);
          this.say(wrongKey ? "That recovery key does not match an account. Nothing changed." : msg.reason);
          if (back.status === "denied") {
            // This device had been stopped (signed in elsewhere): it stays stopped, it does not kick back.
            this.status = "denied";
            this.reason = back.reason;
          } else if (wrongKey) this.reconnect();
          else {
            this.status = "denied";
            this.reason = msg.reason;
          }
          break;
        }
        // A full server is temporary: stay offline and let the normal reconnect try again.
        if (/server full/i.test(msg.reason)) {
          if (!this.full) this.say(msg.reason);
          this.full = true;
          this.retry = Math.max(this.retry, 5);
          break;
        }
        this.status = "denied";
        this.reason = msg.reason;
        break;
      case "saved":
        this.inflight = false;
        this.setRev(msg.rev);
        this.flushSave();
        break;
      case "save":
        this.inflight = false;
        this.pendingSave = null;
        this.setRev(msg.rev);
        this.host.adoptSave(msg.state, msg.why);
        break;
      case "peers":
        for (const p of msg.peers) this.addPeer(p);
        break;
      case "peer":
        this.addPeer(msg.peer);
        break;
      case "pos": {
        const p = this.peers.get(msg.id);
        if (!p) break;
        const far = Math.abs(p.x - msg.x) + Math.abs(p.y - msg.y) > 1;
        p.fromX = far ? msg.x : p.x;
        p.fromY = far ? msg.y : p.y;
        p.x = msg.x;
        p.y = msg.y;
        p.dir = msg.dir;
        p.stepT = 0;
        p.stepMs = msg.run ? 85 : 150;
        p.moving = far ? 0 : 1;
        break;
      }
      case "gone":
        this.peers.delete(msg.id);
        this.requests = this.requests.filter((r) => r.from !== msg.id);
        break;
      case "status": {
        const p = this.peers.get(msg.id);
        if (p) p.status = msg.status;
        break;
      }
      case "chat": {
        const p = this.peers.get(msg.id);
        if (p) p.chat = { text: msg.text, at: now };
        this.chatLog.push({ name: msg.name, text: msg.text, at: now, self: msg.id === this.id });
        this.chatLog = this.chatLog.slice(-6);
        break;
      }
      case "emote": {
        const p = this.peers.get(msg.id);
        if (p) p.emote = { e: msg.e, at: now };
        break;
      }
      case "req":
        this.requests = this.requests.filter((r) => r.from !== msg.from);
        this.requests.push({ kind: msg.kind, from: msg.from, name: msg.name, at: now });
        break;
      case "reqResult":
        if (!msg.accepted) this.notices.push(msg.reason ?? "No answer.");
        break;
      case "duelStart":
        this.duel = msg.duel;
        this.requests = [];
        break;
      case "duelTurn":
        if (msg.next.kind === "end" && this.duel?.id === msg.id && !this.duel.raid) {
          const mine = this.duel.side;
          this.rating = msg.next.rating[mine];
          if (msg.next.winner === mine) this.wins += 1;
          else this.losses += 1;
        }
        break;
      case "trade":
        this.trade = msg.view;
        this.requests = [];
        break;
      case "tradeDone":
      case "tradeClosed":
        this.trade = null;
        break;
      case "top":
        this.top = msg.rows;
        break;
      case "market":
        this.market = msg.listings;
        break;
      case "raid":
        this.raid = msg.view;
        break;
      case "econ":
        this.econ = msg.view;
        break;
      case "wallet":
        this.wallet = msg.wallet;
        break;
      case "crownHouse":
        this.house = msg.view;
        break;
      case "raidKit":
        this.kit = msg.kit;
        break;
      case "raidEnd":
        this.notices.push(...msg.lines);
        break;
      case "raidPool":
        if (this.duel?.id === msg.id && this.duel.raid) this.duel.raid = msg.pool;
        if (this.raid?.pool) this.raid.pool = msg.pool;
        break;
      case "notice":
        this.notices.push(msg.text);
        break;
      case "pong":
        this.online = msg.online;
        break;
    }
    for (const fn of this.listeners) fn(msg);
  }

  private setRev(rev: number): void {
    if (!this.account) this.account = loadAccount();
    this.account.rev = rev;
    saveAccount(this.account);
  }

  private addPeer(p: PeerInfo): void {
    const old = this.peers.get(p.id);
    this.peers.set(p.id, {
      ...p, fromX: p.x, fromY: p.y, stepT: 0, stepMs: 150, moving: 0,
      chat: old?.chat ?? null, emote: old?.emote ?? null,
    });
  }

  /** Advance remote walk clocks; ping now and then for the online count. */
  tick(dt: number): void {
    if (this.live) {
      this.pingT += dt;
      if (this.pingT > 10_000) {
        this.pingT = 0;
        this.send({ t: "ping", at: this.host.now });
      }
    }
    for (const p of this.peers.values()) {
      if (p.moving) {
        p.stepT += dt;
        if (p.stepT >= p.stepMs) {
          p.moving = 0;
          p.fromX = p.x;
          p.fromY = p.y;
        }
      }
      if (p.chat && this.host.now - p.chat.at > 6000) p.chat = null;
      if (p.emote && this.host.now - p.emote.at > 2400) p.emote = null;
    }
    this.requests = this.requests.filter((r) => this.host.now - r.at < 20_000);
  }

  move(x: number, y: number, dir: 0 | 1 | 2 | 3, run: boolean): void {
    const key = `${x},${y},${dir}`;
    if (key === this.lastMove) return;
    this.lastMove = key;
    this.send({ t: "move", x, y, dir, run });
  }

  /** Queue the latest save for the server (debounced; one request in flight). */
  queueSave(state: SaveState): void {
    if (!this.live) return;
    this.pendingSave = state;
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.flushSave();
    }, 1200);
  }

  private flushSave(): void {
    if (!this.pendingSave || this.inflight || !this.account) return;
    const state = this.pendingSave;
    this.pendingSave = null;
    if (this.send({ t: "save", rev: this.account.rev, state })) this.inflight = true;
  }

  takeNotice(): string | null {
    return this.notices.shift() ?? null;
  }
}
